import { useMemo } from 'react';
import type { ThreadMessage } from '../../types/email';
import { Skeleton } from '../../ui/skeleton';
import { UserAvatar } from '../../ui/user-avatar';

export type { ThreadMessage };

export interface ThreadMessageListProps {
  messages: ThreadMessage[] | undefined;
  isLoading: boolean;
}

export function ThreadMessageList({ messages, isLoading }: ThreadMessageListProps) {
  const messagesWithSubjectFlag = useMemo(() => {
    const result: Array<{ message: ThreadMessage; showSubject: boolean }> = [];
    let previousSubject = '';
    for (const message of messages || []) {
      const showSubject = !!(message.subject && message.subject !== previousSubject);
      result.push({ message, showSubject });
      previousSubject = message.subject || previousSubject;
    }
    return result;
  }, [messages]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-3 w-1/5" />
              </div>
            </div>
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-6 text-center">
        <p className="text-xs text-muted-foreground italic">No messages found in this thread.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messagesWithSubjectFlag.map(({ message, showSubject }) => {
        const senderLabel = message.from.name || message.from.email;
        const timestamp = message.timestampMs ? new Date(message.timestampMs) : null;

        return (
          <div key={message.id} className="border rounded-xl bg-card/30 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <UserAvatar
                  user={{ fullName: message.from.name, email: message.from.email }}
                  name={senderLabel}
                  size="md"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{senderLabel}</p>
                  <p className="text-xs text-muted-foreground truncate">{message.from.email}</p>
                </div>
              </div>
              {timestamp && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {timestamp.toLocaleString()}
                </span>
              )}
            </div>
            {showSubject && (
              <p className="font-display text-xs font-normal text-muted-foreground">
                {message.subject}
              </p>
            )}
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {message.bodyText || (
                <span className="italic text-muted-foreground">(No content)</span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}
